import { useState } from 'react'
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/authStore'

type Tab = 'dashboard' | 'users' | 'proxies' | 'sources'

export default function AdminLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { firebaseUser, logout, isLoading, error, clearError } = useAuthStore()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  
  const tabs: { id: Tab; label: string; icon: string; path: string }[] = [
    { id: 'dashboard', label: t('admin.dashboard'), icon: '📊', path: '/admin' },
    { id: 'users', label: t('admin.users'), icon: '👥', path: '/admin/users' },
    { id: 'proxies', label: t('admin.proxies'), icon: '🌐', path: '/admin/proxies' },
    { id: 'sources', label: t('admin.sources'), icon: '🔗', path: '/admin/sources' },
  ]
  
  const activeTab = tabs.find(t => location.pathname.startsWith(t.path))?.id || 'dashboard'
  
  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch {
      // Error handled by store
    }
  }
  
  const displayName = firebaseUser?.displayName || firebaseUser?.email?.split('@')[0] || 'Admin'
  
  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`bg-white border-r border-gray-200 flex flex-col transition-all duration-200 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        {/* Logo */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          {sidebarOpen && <span className="text-lg font-bold text-blue-600">{t('app.adminTitle')}</span>}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>
        
        {/* Tabs */}
        <nav className="flex-1 p-2 space-y-1">
          {tabs.map(tab => (
            <Link
              key={tab.id}
              to={tab.path}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-50 text-blue-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="text-lg">{tab.icon}</span>
              {sidebarOpen && <span>{tab.label}</span>}
            </Link>
          ))}
        </nav>
        
        {/* User info */}
        <div className="p-4 border-t border-gray-200">
          <div className={`flex items-center gap-3 ${sidebarOpen ? '' : 'justify-center'}`}>
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-blue-600 font-medium text-sm">{displayName[0]?.toUpperCase()}</span>
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                <button onClick={handleLogout} className="text-xs text-red-600 hover:underline">
                  {t('nav.signOut')}
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
      
      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
      
      {/* Error toast */}
      {error && (
        <div className="fixed bottom-4 right-4 max-w-md bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg z-50">
          <div className="flex items-start justify-between">
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={clearError} className="text-red-500 hover:text-red-700 ml-4">✕</button>
          </div>
        </div>
      )}
      
      {/* Loading spinner */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 shadow-lg">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      )}
    </div>
  )
}
